<div class="fb-card" [attr.aria-busy]="state().loading || null">
  @if (state().loading) {
    <p class="fb-loading">{{ 'testResults.freigabe.bewertung.loading' | translate }}</p>
  } @else if (state().error) {
    <p class="fb-error" role="alert">{{ 'testResults.freigabe.bewertung.loadError' | translate }}</p>
  } @else if (assessment(); as a) {
    @if (aktualisierungFehler()) {
      <p class="fb-error" role="alert" data-testid="bewertung-aktualisierung-fehler">
        {{ 'testResults.freigabe.bewertung.refreshError' | translate }}
      </p>
    }
    @if (a.phasen.length > 0) {
      <div class="fb-phases">
        <h3 class="fb-section-label">{{ 'testResults.freigabe.bewertung.phases' | translate }}</h3>
        <ol class="fb-phases__list">
          @for (p of a.phasen; track p.key; let last = $last; let i = $index) {
            <li class="fb-phase" [attr.data-state]="phaseState(p)" [attr.aria-current]="p.aktuell ? 'step' : null">
              <span class="fb-phase__node" aria-hidden="true">
                @if (p.done) {
                  <svg class="fb-phase__check" viewBox="0 0 24 24" focusable="false">
                    <path d="M5 13l4 4 10-10" />
                  </svg>
                } @else {
                  <span class="fb-phase__dot"></span>
                }
              </span>
              <span class="fb-phase__label">{{ p.label }}</span>
              <span class="fb-phase__date">{{ formatDate(p.datum) }}</span>
              <span class="sr-only">
                {{ 'testResults.freigabe.bewertung.phaseState.' + phaseState(p) | translate }}
              </span>
            </li>
            @if (!last) {
              <li
                class="fb-phases__connector"
                role="presentation"
                aria-hidden="true"
                [class.fb-phases__connector--active]="a.phasen[i + 1].done || a.phasen[i + 1].aktuell"
              ></li>
            }
          }
        </ol>
      </div>
    }

    <div class="fb-grid">
      <div class="fb-left">
        <h3 class="fb-section-label">{{ 'testResults.freigabe.bewertung.gatesTitle' | translate }}</h3>
        <div class="fb-gates" role="table" [attr.aria-label]="'testResults.freigabe.bewertung.gatesTitle' | translate">
          <div class="fb-gates__head" role="row">
            <span class="fb-gates__col fb-gates__col--status" role="columnheader">
              <span class="sr-only">{{ 'testResults.freigabe.bewertung.columns.status' | translate }}</span>
            </span>
            <span class="fb-gates__col fb-gates__col--label" role="columnheader">
              {{ 'testResults.freigabe.bewertung.columns.criterion' | translate }}
            </span>
            <span class="fb-gates__col fb-gates__col--kind" role="columnheader">
              {{ 'testResults.freigabe.bewertung.columns.kind' | translate }}
            </span>
            <span class="fb-gates__col fb-gates__col--soll" role="columnheader">
              {{ 'testResults.freigabe.bewertung.columns.target' | translate }}
            </span>
            <span class="fb-gates__col fb-gates__col--ist" role="columnheader">
              {{ 'testResults.freigabe.bewertung.columns.actual' | translate }}
            </span>
          </div>
          @for (k of a.kriterien; track k.key) {
            <div class="fb-gate" role="row" data-testid="quality-gate-row" [attr.data-tone]="gateTone(k)">
              <span class="fb-gates__col fb-gates__col--status" role="cell">
                <span class="fb-gate__status">
                  @if (k.ok) {
                    <svg class="fb-gate__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M5 13l4 4 10-10" />
                    </svg>
                  } @else {
                    <svg class="fb-gate__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M6 6l12 12M6 18L18 6" />
                    </svg>
                  }
                  <span class="sr-only">
                    {{ 'testResults.freigabe.bewertung.gateState.' + (k.ok ? 'ok' : 'fail') | translate }}
                  </span>
                </span>
              </span>
              <span class="fb-gates__col fb-gates__col--label fb-gate__label" role="cell">
                {{ 'testResults.freigabe.bewertung.criteria.' + k.key + '.label' | translate }}
              </span>
              <span class="fb-gates__col fb-gates__col--kind" role="cell">
                <span class="fb-gate__kind" [attr.data-kind]="k.muss ? 'muss' : 'soll'">
                  {{ 'testResults.freigabe.bewertung.kind.' + (k.muss ? 'muss' : 'soll') | translate }}
                </span>
              </span>
              <span class="fb-gates__col fb-gates__col--soll fb-gate__soll" role="cell">
                {{ 'testResults.freigabe.bewertung.criteria.' + k.key + '.soll' | translate: { wert: k.sollWert } }}
              </span>
              <span class="fb-gates__col fb-gates__col--ist fb-gate__ist" role="cell">
                @if (k.istWert === null) {
                  —
                } @else {
                  {{
                    'testResults.freigabe.bewertung.criteria.' + k.key + '.ist'
                      | translate: { wert: k.istWert, nenner: k.istNenner }
                  }}
                }
              </span>
            </div>
          }
        </div>

        <div class="fb-prio">
          <div class="fb-exec">
            <h4 class="fb-prio__label">{{ 'testResults.freigabe.bewertung.execByPrio' | translate }}</h4>
            @for (prio of priorities; track prio) {
              @let x = a.ausfuehrungNachPrio[prio];
              @let pct = percent(x.aus, x.ges);
              <div class="fb-exec__row">
                <span class="fb-exec__label">{{ 'testCases.priority.' + prio | translate }}</span>
                <span class="fb-exec__track" aria-hidden="true">
                  <span class="fb-exec__fill" [class.fb-exec__fill--full]="pct === 100" [style.width.%]="pct"></span>
                </span>
                <span class="fb-exec__count">{{ x.aus }}/{{ x.ges }}</span>
              </div>
            }
          </div>
          <div class="fb-defects">
            <h4 class="fb-prio__label">{{ 'testResults.freigabe.bewertung.defectsByPrio' | translate }}</h4>
            @for (prio of priorities; track prio) {
              @let n = a.fehlerNachPrio[prio];
              <div class="fb-defects__row" [attr.data-prio]="prio">
                <span class="fb-defects__label">{{ 'testCases.priority.' + prio | translate }}</span>
                <span class="fb-defects__stripes" aria-hidden="true">
                  @for (s of stripes(n); track s) {
                    <span class="fb-defects__stripe"></span>
                  }
                </span>
                <span class="fb-defects__count" [class.fb-defects__count--none]="!n">{{ n || '—' }}</span>
              </div>
            }
          </div>
        </div>
      </div>

      <div class="fb-right">
        <h3 class="fb-section-label">{{ 'testResults.freigabe.bewertung.decisionTitle' | translate }}</h3>
        <app-freigabe-entscheidung
          [projectId]="projectId()"
          [releaseId]="releaseId()"
          [assessment]="a"
          [teststufe]="teststufe()"
          (assessmentChange)="onAssessmentChange($event)"
          (protokollExportieren)="protokollExportieren.emit()"
        />
      </div>
    </div>
  }
</div>
